'use client';

import type { MutableRefObject, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import immer from 'immer';
import invariant from 'invariant';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import { useApolloClient } from '@frameio/graphql-client';
import { useActiveAccountId } from '@frameio/shared/contexts';
import {
  HydrateSelectedAssetTypes,
  type HydrateSelectedAssetTypesQuery,
} from './gql/generated/HydrateSelectedAssetTypes';
import { type AssetType, getAssetTypename } from './utils';

export type AssetTypeMap = Record<string, AssetType | undefined>;

type SelectionContext = {
  assetTypeMap: AssetTypeMap;
  focusedId: string | null;
  selectionSet: Set<string>;
  stableSelectionSet: MutableRefObject<Set<string>>;
  setState: React.Dispatch<
    React.SetStateAction<Omit<SelectionContext, 'setState'>>
  >;
};

export const DEFAULT_VALUE: SelectionContext = {
  assetTypeMap: {},
  focusedId: null,
  selectionSet: new Set(),
  setState: noop,
  stableSelectionSet: { current: new Set() },
};

const SelectionContext = createContext(DEFAULT_VALUE);

type Props = PropsWithChildren<{ value?: Omit<SelectionContext, 'setState'> }>;

/**
 * The SelectionContext provides the current set of asset ids selected
 * in Finder2 and operations to update this selection.
 */
export function SelectionContextProvider({
  children,
  value: initialValue = DEFAULT_VALUE,
}: Props) {
  const [state, setState] = useState(initialValue);
  const accountId = useActiveAccountId();
  const stableSelectionSet = useRef(state.selectionSet);
  const [assetTypeMap, setAssetTypeMap] = useState<AssetTypeMap>({});
  const value = useMemo<SelectionContext>(
    () => ({
      ...state,
      assetTypeMap,
      stableSelectionSet,
      setState,
    }),
    [state, assetTypeMap]
  );

  const client = useApolloClient();

  useEffect(() => {
    let cancelled = false;
    stableSelectionSet.current = state.selectionSet;

    /**
     * For each selected id, reuse a typename we already resolved on a prior
     * selection; otherwise try the Apollo cache, and fall back to a server
     * query for anything still unresolved (e.g. off-screen virtualized
     * assets that haven't been hydrated). `assetTypeMap` is read but
     * intentionally excluded from the dep array — we want the value from
     * the previous commit as a kind of cache, and re-running on our own
     * write would loop.
     */
    const nextAssetTypeMap: AssetTypeMap = {};
    const unresolvedIds: string[] = [];

    state.selectionSet.forEach((id) => {
      const previousType = assetTypeMap[id];
      if (previousType) {
        nextAssetTypeMap[id] = previousType;
        return;
      }
      const apolloCachedType = getAssetTypename(client.cache, id)?.__typename;
      if (apolloCachedType) {
        nextAssetTypeMap[id] = apolloCachedType;
      } else {
        unresolvedIds.push(id);
      }
    });

    setAssetTypeMap(nextAssetTypeMap);

    if (unresolvedIds.length > 0 && accountId) {
      client
        .query<HydrateSelectedAssetTypesQuery>({
          query: HydrateSelectedAssetTypes,
          variables: {
            accountId,
            ids: unresolvedIds,
            first: unresolvedIds.length,
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'ignore',
        })
        .then(({ data }) => {
          if (cancelled) return;

          const resolved: AssetTypeMap = {};
          data?.account?.assets?.nodes.forEach((asset) => {
            resolved[asset.id] = asset.__typename;
          });

          setAssetTypeMap((prev) => ({ ...prev, ...resolved }));
        })
        .catch(noop);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above re: assetTypeMap
  }, [accountId, client, state.selectionSet]);

  return (
    <SelectionContext.Provider value={value}>
      <>{children}</>
    </SelectionContext.Provider>
  );
}

/**
 * Get the count of selected assets.
 */
export const useSelectedAssetsCount = () =>
  useContextSelector(SelectionContext, (ctx) => {
    const { selectionSet } = ctx;
    return selectionSet.size;
  });

/**
 * Get the ids of the selected assets.
 */
export const useSelectedAssets = () =>
  useContextSelector(SelectionContext, (ctx) => {
    const { selectionSet } = ctx;
    return selectionSet;
  });

export const useGetSelectedAssets = () => {
  const selectionSet = useContextSelector(
    SelectionContext,
    (ctx) => ctx.selectionSet
  );
  return useMemo(() => Array.from(selectionSet), [selectionSet]);
};

/**
 * useGetIsIdSelected returns a function which can be run to determine if an ID is
 * in the selection.
 * For imperative use cases
 *
 * @returns getIsIdSelected a function which, given an id, returns if the id is selected or not
 */
export const useGetIsAssetSelected = () => {
  const selectionSet = useContextSelector(
    SelectionContext,
    (ctx) => ctx.selectionSet
  );
  return useCallback((id: string) => selectionSet.has(id), [selectionSet]);
};

/**
 * useIsIdSelected returns true if the passed asset id is part of the selection
 *
 * @param id an asset id
 * @returns {boolean} Is the passed id part of the current selection?
 */
export const useIsAssetSelected = (id: string) =>
  useContextSelector(SelectionContext, (ctx) => ctx.selectionSet.has(id));

/**
 * useSelectId returns a function for adding items to the selection.
 *
 * @returns selectId a function which select the given id when run
 */
export const useSelectAsset = () => {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);
  return useCallback(
    (id: string, preserveSelection = false) => {
      setState(
        immer((draft) => {
          if (preserveSelection) {
            draft.selectionSet.add(id);
          } else {
            draft.selectionSet = new Set([id]);
          }
        })
      );
    },
    [setState]
  );
};

/**
 * Select some assets
 *
 * @param ids A list of asset ids to select.
 * @returns a function which select assets passed as an argument
 */
export const useSelectAssets = () => {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);

  return useCallback(
    (ids: string[]) => {
      setState(
        immer((draft) => {
          ids.forEach((id) => {
            draft.selectionSet.add(id);
          });
        })
      );
    },
    [setState]
  );
};

/**
 * useSelectRange provides a function for (de)selecting a range of assets.
 *
 * @returns selectRange a function which (de)selects asset ids from the last
 *                      selected id, up to a given target id
 */
export const useSelectAssetRange = () => {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);

  /**
   * selectRange
   *
   * Select or deselect a range of asset ids up to the targetId. Handles forward
   * and backward selections.
   *
   * @param ids the entire list of selectable asset ids
   * @param targetId the id of the asset that was selected for selection to reach
   * @param exclude if true, remove the calculated range from the selection
   */
  return useCallback(
    (ids: string[], targetId: string, exclude = false) => {
      setState(
        immer((draft) => {
          let startIdIndex = -1;
          let endIdIndex = -1;

          const lastSelectedId = Array.from(draft.selectionSet).pop();

          ids.forEach((value, index) => {
            if (value === lastSelectedId) startIdIndex = index;
            if (value === targetId) endIdIndex = index;
          });

          if (!lastSelectedId) startIdIndex = 0;

          if (startIdIndex === -1) {
            invariant(startIdIndex, 'lastSelectedId not found in ids array');
            return;
          }

          if (endIdIndex === -1) {
            invariant(endIdIndex, 'targetId not found in ids array');
            return;
          }

          let range: string[];

          if (endIdIndex < startIdIndex) {
            range = ids.slice(endIdIndex, startIdIndex + 1);
            // we specifically want to run through this loop backwards so that
            // we ensure that the targetId always gets inserted into the set
            // LAST. this makes it so the lastSelectedId is always the asset that
            // was last clicked/targeted
            range.reverse();
          } else {
            range = ids.slice(startIdIndex, endIdIndex + 1);
          }

          range.forEach((id) => {
            if (exclude) {
              draft.selectionSet.delete(id);
            } else {
              draft.selectionSet.add(id);
            }
          });
        })
      );
    },
    [setState]
  );
};

/**
 * useDeselectId provides a function for removing items from the selection.
 *
 * @returns deselectId a function which deselect a given id when run
 */
export const useDeselectAsset = () => {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);

  return useCallback(
    (id: string) => {
      setState(
        immer((draft) => {
          draft.selectionSet.delete(id);
        })
      );
    },
    [setState]
  );
};

/**
 * Deselect all assets
 *
 * @param ids A optional list of asset ids to deselect, when undefined, the selection set is emptied.
 * @returns a function which deselect all assets or the list of assets passed.
 */
export const useDeselectAssets = () => {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);

  return useCallback(
    (ids?: string[]) => {
      setState(
        immer((draft) => {
          if (ids) {
            ids.forEach((id) => {
              draft.selectionSet.delete(id);
            });
          } else {
            draft.selectionSet = new Set();
          }
        })
      );
    },
    [setState]
  );
};

/**
 * useGetClearAssetSelection provides a function for clearing the selection
 *
 * @returns clearSelection a function which resets the selection
 */
export const useClearAssetSelection = () => {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);

  return useCallback(() => {
    setState(
      immer((draft) => {
        draft.selectionSet = new Set();
      })
    );
  }, [setState]);
};

/**
 * useFocusId provides a callback that can be used to update the
 * focused asset id.
 *
 * @returns setFocusedId callback for updating the focused asset id
 */
export function useSetFocusedAsset() {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);

  return useCallback(
    (focusedId: string | null) => {
      setState(
        immer((draft) => {
          draft.focusedId = focusedId;
        })
      );
    },
    [setState]
  );
}

/**
 * useFocusedId returns the id of the asset that is currently focused. A
 * focused asset is an asset that is being operated on asynchronously. For example,
 * if a user wants to copy an asset, we can store the id of "focused" asset at the
 * time they click the "Copy" button in the context menu. This lets the context
 * menu know which asset is being targeted even though the context menu is now
 * closed.
 *
 * This is a very specific solve for a very specific problem. The useContextMenuTarget
 * should be favored as much as possible over reading this value.
 *
 * @returns assetId The asset id
 */
export const useFocusedAsset = () =>
  useContextSelector(SelectionContext, ({ focusedId }) => focusedId);

/**
 * Helper hook for checking if a given id is currently focused while limiting
 * rerenders.
 *
 * @returns isFocused Whether or not the id is focused
 */
export const useIsAssetFocused = (id: string) =>
  useContextSelector(SelectionContext, ({ focusedId }) => focusedId === id);

/**
 * useClearFocusedId provides a convenience function for nulling the
 * focused asset id.
 *
 * @returns clearFocusedAsset callback that nulls out the focused asset id
 */
export const useClearFocusedAsset = () => {
  const setState = useContextSelector(SelectionContext, (ctx) => ctx.setState);

  return useCallback(() => {
    setState(
      immer((draft) => {
        draft.focusedId = null;
      })
    );
  }, [setState]);
};

/**
 * The useStableSelectionSet provides a read-only copy of the selection via a
 * ref in order to read from the selection while bypassing re-renders caused by
 * selection changes.
 *
 * @returns stableSelectionSet The selection ref
 */
export const useStableSelectionSet = () =>
  useContextSelector(SelectionContext, (ctx) => ctx.stableSelectionSet);

/**
 * The useSelectionAssetTypeMap provides a read-only mapping of asset id => asset
 * __typename that is locally cached in this context. When the selection changes,
 * types are read from the Apollo cache for hydrated items and fetched from the
 * server for any remaining items (e.g., off-screen virtualized assets).
 *
 * @returns assetTypeMap A plain object mapping asset id to __typename
 */
export const useSelectionAssetTypeMap = () =>
  useContextSelector(SelectionContext, (ctx) => ctx.assetTypeMap);
